import type { BuilderNode } from "../types";

export const FORM_BUILDER_DROP_ROOT = "drop-root";

export const FORM_BUILDER_DROP_SECTION_PREFIX = "drop-section::";

/** Narrow hit target for “append field” strip (drag `isOver` + same index as section container). */
export const FORM_BUILDER_DROP_SECTION_FIELD_STRIP_PREFIX =
	"drop-section-field::";

export function formBuilderDropSectionId(sectionId: string): string {
	return `${FORM_BUILDER_DROP_SECTION_PREFIX}${sectionId}`;
}

export function formBuilderDropSectionFieldStripId(sectionId: string): string {
	return `${FORM_BUILDER_DROP_SECTION_FIELD_STRIP_PREFIX}${sectionId}`;
}

/**
 * Section id when `over` is the broad section droppable or the field-strip droppable (append index).
 */
export function resolveSectionAppendFromDropOverId(
	overId: string,
	nodes: Record<string, BuilderNode>,
): string | null {
	if (overId.startsWith(FORM_BUILDER_DROP_SECTION_FIELD_STRIP_PREFIX)) {
		const sectionId = overId.slice(
			FORM_BUILDER_DROP_SECTION_FIELD_STRIP_PREFIX.length,
		);
		return nodes[sectionId]?.kind === "section" ? sectionId : null;
	}
	if (overId.startsWith(FORM_BUILDER_DROP_SECTION_PREFIX)) {
		const sectionId = overId.slice(FORM_BUILDER_DROP_SECTION_PREFIX.length);
		return nodes[sectionId]?.kind === "section" ? sectionId : null;
	}
	return null;
}

export interface FormBuilderDropRootTarget {
	kind: "root";
	index: number;
}

export interface FormBuilderDropSectionTarget {
	kind: "section";
	sectionId: string;
	index: number;
}

export type FormBuilderResolvedDropTarget =
	| FormBuilderDropRootTarget
	| FormBuilderDropSectionTarget;

interface ResolveContext {
	nodes: Record<string, BuilderNode>;
	rootIds: string[];
}

/**
 * Maps `over.id` from @dnd-kit to an insert index for root or a section.
 * Insert index is **before** the referenced field when `over` is a field; append when `over` is a section container id or `drop-section::`.
 */
export function resolveFormBuilderDropTarget(
	overId: string,
	ctx: ResolveContext,
): FormBuilderResolvedDropTarget | null {
	const { nodes, rootIds } = ctx;

	if (overId === FORM_BUILDER_DROP_ROOT) {
		return { kind: "root", index: rootIds.length };
	}

	const appendSectionId = resolveSectionAppendFromDropOverId(overId, nodes);
	if (appendSectionId !== null) {
		const parent = nodes[appendSectionId];
		if (parent?.kind !== "section") return null;
		return {
			kind: "section",
			sectionId: appendSectionId,
			index: parent.childFieldIds.length,
		};
	}

	const overNode = nodes[overId];
	if (!overNode) return null;

	if (rootIds.includes(overId)) {
		if (overNode.kind === "section") {
			return {
				kind: "section",
				sectionId: overId,
				index: overNode.childFieldIds.length,
			};
		}
		if (overNode.kind === "field") {
			return { kind: "root", index: rootIds.indexOf(overId) };
		}
		return null;
	}

	if (overNode.kind === "field") {
		const parentId = overNode.parentId;
		if (parentId === "root") {
			return { kind: "root", index: rootIds.indexOf(overId) };
		}
		const parent = nodes[parentId];
		if (parent?.kind !== "section") return null;
		const idx = parent.childFieldIds.indexOf(overId);
		if (idx === -1) return null;
		return { kind: "section", sectionId: parentId, index: idx };
	}

	return null;
}
